import { useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { MdArrowDownward, MdArrowUpward, MdUnfoldMore } from "react-icons/md";
import Skeleton from "components/skeleton/Skeleton";
import EmptyState from "components/states/EmptyState";
import Pagination from "components/data-table/Pagination";

const SKELETON_ROWS = 5;
const DEFAULT_PAGE_SIZE = 10;
const INTERACTIVE_SELECTOR = "a, button, input, select, textarea, label, [role='button'], [role='menuitem']";

const ALIGN = { right: "text-right", center: "text-center" };
// Stable default: a fresh [] each render would retrigger TanStack's row model/page reset.
const NO_ROWS = [];

const cx = (...classes) => classes.filter(Boolean).join(" ");

const ARIA_SORT = { asc: "ascending", desc: "descending" };

/**
 * Table on TanStack Table with the standard header/cell styles, loading skeleton,
 * empty state, optional sorting, clickable rows and pagination.
 *
 * Column `meta`: `align: "right"|"center"`, `className` (applied to th and td).
 * Explicit `size` on a column sets its width. `enableSorting: false` opts a column out.
 *
 * @param {object} props
 * @param {import("@tanstack/react-table").ColumnDef[]} props.columns
 * @param {Array} [props.data=[]] Rows (current page only in server mode).
 * @param {boolean} [props.loading=false] Shows 5 skeleton rows.
 * @param {React.ReactNode} [props.emptyState] Rendered full-width when there are no rows.
 * @param {(row: any, event: Event) => void} [props.onRowClick] Makes rows focusable and
 *   clickable (Enter/Space). Clicks on links/buttons inside a row are ignored.
 * @param {true|{pageIndex?, pageSize?, total?, onPageChange?, onPageSizeChange?, pageSizeOptions?}} [props.pagination]
 *   `true` → client-side paging of `data` (10 per page). An object with `onPageChange`
 *   → controlled server paging (`pageIndex` zero-based, `total` = all rows). An object
 *   without `onPageChange` → client-side with the given `pageSize`/`pageSizeOptions`.
 * @param {Array<{id: string, desc: boolean}>} [props.sorting] Enables sorting. Controlled
 *   with `onSortingChange`, otherwise the initial sort.
 * @param {(updater) => void} [props.onSortingChange] Controlled sorting (TanStack updater).
 *   In server-pagination mode the parent sorts (manual sorting); else rows sort client-side.
 * @param {(row: any, index: number) => string} [props.getRowId] Stable row keys (e.g. `row => row.id`).
 * @param {string} [props["aria-label"]] Accessible name for the table.
 * @param {"card"|"plain"} [props.variant="card"] `card`: bordered, rounded surface of its own.
 *   `plain`: no border, radius or background, for a table inside a Card (e.g. under a CardHeader).
 * @param {object} [props.meta] TanStack table `meta`, readable in cells via `table.options.meta`.
 * @param {string} [props.className] Wrapper classes. The wrapper is `relative`, so a
 *   `Loader variant="overlay"` placed by the caller covers the table.
 */
const DataTable = ({
  columns,
  data = NO_ROWS,
  loading = false,
  emptyState,
  onRowClick,
  pagination,
  sorting,
  onSortingChange,
  getRowId,
  "aria-label": ariaLabel,
  variant = "card",
  meta,
  className = "",
}) => {
  const serverPaging = Boolean(pagination && typeof pagination.onPageChange === "function");
  const clientPaging = Boolean(pagination) && !serverPaging;
  const sortingEnabled = sorting !== undefined || typeof onSortingChange === "function";
  const controlledSorting = typeof onSortingChange === "function";

  const [internalSorting, setInternalSorting] = useState(sorting ?? []);
  const [clientPagination, setClientPagination] = useState({
    pageIndex: 0,
    pageSize: (clientPaging && pagination.pageSize) || DEFAULT_PAGE_SIZE,
  });

  const state = {};
  if (sortingEnabled) state.sorting = controlledSorting ? sorting ?? [] : internalSorting;
  if (clientPaging) state.pagination = clientPagination;

  const table = useReactTable({
    data,
    columns,
    state,
    getRowId,
    meta,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: sortingEnabled,
    manualSorting: serverPaging,
    onSortingChange: controlledSorting ? onSortingChange : setInternalSorting,
    ...(sortingEnabled && !serverPaging ? { getSortedRowModel: getSortedRowModel() } : {}),
    ...(clientPaging
      ? { getPaginationRowModel: getPaginationRowModel(), onPaginationChange: setClientPagination }
      : {}),
  });

  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length || 1;
  const interactive = typeof onRowClick === "function";

  const activateRow = (row, event) => {
    const target = event.target.closest?.(INTERACTIVE_SELECTOR);
    if (target && target !== event.currentTarget && event.currentTarget.contains(target)) return;
    onRowClick(row.original, event);
  };

  const handleRowKeyDown = (row, event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onRowClick(row.original, event);
    }
  };

  let footer = null;
  if (serverPaging && pagination.total > 0) {
    footer = (
      <Pagination
        pageIndex={pagination.pageIndex ?? 0}
        pageSize={pagination.pageSize ?? DEFAULT_PAGE_SIZE}
        total={pagination.total}
        onPageChange={pagination.onPageChange}
        onPageSizeChange={pagination.onPageSizeChange}
        pageSizeOptions={pagination.pageSizeOptions}
      />
    );
  } else if (clientPaging && !loading) {
    const total = table.getPrePaginationRowModel().rows.length;
    if (total > 0) {
      footer = (
        <Pagination
          pageIndex={clientPagination.pageIndex}
          pageSize={clientPagination.pageSize}
          total={total}
          onPageChange={(pageIndex) => table.setPageIndex(pageIndex)}
          onPageSizeChange={(pageSize) => setClientPagination({ pageIndex: 0, pageSize })}
          pageSizeOptions={pagination.pageSizeOptions}
        />
      );
    }
  }

  const renderBody = () => {
    if (loading) {
      return Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
        <tr key={`skeleton-${rowIndex}`}>
          {table.getVisibleLeafColumns().map((column) => (
            <td key={column.id} className="border-t border-line px-4 py-3">
              <Skeleton className="h-4 w-3/4" />
            </td>
          ))}
        </tr>
      ));
    }

    if (rows.length === 0) {
      return (
        <tr>
          <td colSpan={columnCount} className="border-t border-line">
            {emptyState ?? <EmptyState variant="inline" title="No results" />}
          </td>
        </tr>
      );
    }

    return rows.map((row) => (
      <tr
        key={row.id}
        {...(interactive
          ? {
              tabIndex: 0,
              onClick: (event) => activateRow(row, event),
              onKeyDown: (event) => handleRowKeyDown(row, event),
              className:
                "cursor-pointer transition-colors hover:bg-surface-subtle focus-visible:bg-surface-subtle focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus",
            }
          : {})}
      >
        {row.getVisibleCells().map((cell) => {
          const meta = cell.column.columnDef.meta ?? {};
          return (
            <td
              key={cell.id}
              className={cx(
                "border-t border-line px-4 py-3 text-sm text-fg",
                ALIGN[meta.align],
                meta.className
              )}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          );
        })}
      </tr>
    ));
  };

  return (
    <div
      className={cx(
        "relative",
        variant !== "plain" && "overflow-hidden rounded-card border border-line bg-surface",
        className
      )}
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse" aria-label={ariaLabel} aria-busy={loading || undefined}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-surface-raised">
                {headerGroup.headers.map((header) => {
                  const { column } = header;
                  const meta = column.columnDef.meta ?? {};
                  const canSort = column.getCanSort();
                  const sorted = column.getIsSorted();
                  const content = header.isPlaceholder
                    ? null
                    : flexRender(column.columnDef.header, header.getContext());
                  const SortIcon =
                    sorted === "asc" ? MdArrowUpward : sorted === "desc" ? MdArrowDownward : MdUnfoldMore;

                  return (
                    <th
                      key={header.id}
                      scope="col"
                      colSpan={header.colSpan}
                      aria-sort={canSort ? ARIA_SORT[sorted] ?? "none" : undefined}
                      style={column.columnDef.size ? { width: header.getSize() } : undefined}
                      className={cx(
                        "whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-fg-muted",
                        ALIGN[meta.align],
                        meta.className
                      )}
                    >
                      {canSort ? (
                        <button
                          type="button"
                          onClick={column.getToggleSortingHandler()}
                          className={cx(
                            "focus-ring inline-flex items-center gap-1 rounded uppercase tracking-wide transition-colors hover:text-fg",
                            sorted && "text-fg"
                          )}
                        >
                          {content}
                          <SortIcon
                            aria-hidden="true"
                            className={cx("h-4 w-4 shrink-0", !sorted && "opacity-50")}
                          />
                        </button>
                      ) : (
                        content
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>{renderBody()}</tbody>
        </table>
      </div>
      {footer && <div className="border-t border-line px-4 py-3">{footer}</div>}
    </div>
  );
};

export default DataTable;
